import debounce from "lodash-es/debounce"

export type TabTooltip = string | [title: string, text: string]

export interface TabItem {
  value: string
  label: string
  icon?: string
  tooltip?: TabTooltip
  scope?: AuthScope
}

export function useTabs() {
  const route = useRoute()
  const router = useRouter()

  const tabs = computed<Array<TabItem>>(() => (Array.isArray(route.meta.tabs) ? route.meta.tabs : []))

  const getValidTab = async (tab: string) => {
    if (tabs.value.some((t) => t.value === tab)) {
      return tab
    }

    return tabs.value[0]?.value ?? undefined
  }

  const updateQuery = debounce(async (tab?: string) => {
    const tabChange = await getValidTab(tab ?? (route.query.tab as string) ?? "")

    if (route.query.tab === tabChange) {
      return
    }

    const { title, organizationId } = route.meta

    await router.replace({
      query: {
        ...route.query,
        tab: tabChange,
      },
    })

    route.meta.title = title
    route.meta.organizationId = organizationId

    window.scrollTo(0, 0)
  }, 10)

  const tabsKey = computed(() => tabs.value.map((tab) => tab.value).join("-"))

  const selectedTab = computed({
    get: () => route.query.tab as string | undefined,
    set: (newTab: string) => {
      updateQuery(newTab)
    },
  })

  watch(
    tabs,
    () => {
      updateQuery()
    },
    { deep: true, immediate: true },
  )

  watch(
    () => route.query.tab,
    () => {
      updateQuery()
    },
    { immediate: true, deep: true },
  )

  return { tabs, selectedTab, tabsKey }
}
